import { useState, useMemo, useCallback } from 'react';
import moment from 'moment-timezone';
import { expandTimeslotsForDay, parseAppointmentLengthMinutes } from '../../utils/expand-timeslots';

interface AvailableTime {
  date: string
  availableTimeslots: { start: string; end: string }[]
}

interface Timeslot {
  start: string
  end: string
}

const useTimeslotSelection = (
  availableTimeList: AvailableTime[] | undefined,
  selectedDate: string,
  appointmentLength: string,
) => {
  const [selectedSlotStart, setSelectedSlotStart] = useState('');

  const availableTimeslots: Timeslot[] = useMemo(() => {
    if (!availableTimeList || !selectedDate) return [];
    const dayData = availableTimeList.find((at) => at.date === selectedDate);
    const rawSlots = dayData?.availableTimeslots ?? [];
    const appointmentLengthMinutes = parseAppointmentLengthMinutes(appointmentLength);
    return expandTimeslotsForDay(selectedDate, rawSlots, appointmentLengthMinutes);
  }, [availableTimeList, selectedDate, appointmentLength]);

  const selectTimeslot = useCallback(
    (slotStart: string, slotEnd: string, timezone: string) => {
      const format = 'YYYY-MM-DDTHH:mmZ';
      const browserTimezone = moment.tz.guess();
      setSelectedSlotStart(slotStart);
      const start = moment.tz(`${selectedDate} ${slotStart}`, 'YYYY-MM-DD HH:mm', browserTimezone);
      const end = moment.tz(`${selectedDate} ${slotEnd}`, 'YYYY-MM-DD HH:mm', browserTimezone);
      // Overnight slot (e.g. 23:20 -> 00:05): the end time belongs to the next day.
      if (end.isSameOrBefore(start)) {
        end.add(1, 'day');
      }
      return {
        startDateTime: start.tz(timezone).format(format),
        endDateTime: end.tz(timezone).format(format),
      };
    },
    [selectedDate],
  );

  const resetSelection = useCallback(() => {
    setSelectedSlotStart('');
  }, []);

  return {
    selectedSlotStart,
    availableTimeslots,
    selectTimeslot,
    resetSelection,
  };
};

export default useTimeslotSelection;
